import { z } from 'zod';

export const submitDipApplicationPathParamsSchema = z.object({
  agreementReference: z.string().describe('Agreement Reference'),
  applicationId: z.string().describe('The Application Id'),
});

/**
 * @description Success response containing the ID of the created application
 */
export const submitDipApplication200Schema = z.object({ status: z.number() });

/**
 * @description Bad Request Error
 */
export const submitDipApplication400Schema = z.object({
  message: z.string().describe('The returned error message'),
  paths: z.array(z.string()).describe('List of details about the bad request error'),
  status: z.number(),
});

/**
 * @description Not Found Error
 */
export const submitDipApplication404Schema = z.object({
  message: z.string().describe('The returned error message'),
  status: z.number(),
});

/**
 * @description Default error response
 */
export const submitDipApplication500Schema = z.object({
  message: z.enum(['Internal Server Error']),
  status: z.number(),
});

export const submitDipApplicationMutationRequestSchema = z.object({
  applicantTwoEmploymentDetails: z
    .object({
      employmentType: z.enum(['Employed', 'Self-employed', 'Contractor', 'Retired', 'No employment']),
      netIncomePreviousYear: z.string().optional(),
      grossAnnualIncome: z.string().optional(),
      grossIncomePreviousYear: z.string().optional(),
      otherEmploymentDetails: z.string().optional(),
      yearsOfIncomeProof: z.enum(['1 year', '2 years+']).optional(),
      otherIncomeTypes: z.array(z.string()).optional(),
      employedType: z.enum(['Permanent', 'Temporary']).optional(),
      anyOtherIncome: z.boolean().optional(),
      employmentStartDate: z.string().optional(),
      willContractBeRenewed: z.boolean().optional(),
      tradingLengthYears: z.string().optional(),
      netMonthlyIncome: z.string().optional(),
      anyOtherEmployment: z.boolean().optional(),
      grossIncomeRecentYear: z.string().optional(),
      monthsLeftOnContract: z.enum(['Under 3 months', '3-6 months', '6-12 months', '12 months+']).optional(),
      totalOtherIncomeAverage: z.string().optional(),
      netIncomeRecentYear: z.string().optional(),
    })
    .optional(),
  userDetails: z.object({
    applicantTwoDateOfBirth: z.string().optional(),
    applicantTwoResidentialStatus: z
      .enum(['Renting', 'Living with friends and family', 'Own home', 'Own home with finance'])
      .optional(),
    address: z.string(),
    applicantTwoResidencyStatusOther: z.string().optional(),
    numberOfDependents: z.string().optional(),
    city: z.string(),
    hasSecondApplicant: z.boolean(),
    applicantTwoAuthorityToSubmit: z.boolean().optional(),
    residencyStatusOther: z.string().optional(),
    postcode: z.string(),
    applicantTwoRelationship: z
      .enum(['Spouse / Civil Partner', 'Fianc\u00E9 / Fianc\u00E9e', 'Parent', 'Child', 'Sibling', 'Friend'])
      .optional(),
    hasDependents: z.boolean(),
    dateOfBirth: z.string(),
    residencyStatus: z.enum(['British Citizen', 'Indefinite Leave to Remain', 'Expat', 'Other']),
    phoneNumber: z.string(),
    residentialStatus: z.enum(['Renting', 'Living with friends and family', 'Own home', 'Own home with finance']),
    name: z.string(),
    applicantTwoMaritalStatus: z
      .enum([
        'Single',
        'Married / Civil Partnership',
        'Widowed',
        'Divorced / Ex-civil Partnership',
        'Engaged',
        'Separated',
      ])
      .optional(),
    email: z.string(),
    maritalStatus: z.enum([
      'Single',
      'Married / Civil Partnership',
      'Widowed',
      'Divorced / Ex-civil Partnership',
      'Engaged',
      'Separated',
    ]),
    applicantTwoSameAddress: z.boolean().optional(),
    applicantTwoResidencyStatus: z.enum(['British Citizen', 'Indefinite Leave to Remain', 'Expat', 'Other']).optional(),
  }),
  timeFrame: z.string(),
  outgoingsAndCommitments: z.object({
    insurance: z.string().min(1),
    subscriptions: z.string().min(1),
    medical: z.string().min(1),
    otherShopping: z.string().min(1),
    totalMonthlyRent: z.enum(['I will not pay any rent/board expense after completion', 'Other']),
    childcareAndSchool: z.string().min(1),
    totalMonthlyPayment: z.string().optional(),
    financialDifficulty: z.boolean(),
    eatingOut: z.string().min(1),
    utilities: z.string().min(1),
    lifestyle: z.string().min(1),
    childMaintenance: z.string().min(1),
    inRentArrears: z.boolean(),
    totalMonthlyRentOther: z.string().min(1).optional(),
    financialCommitments: z.boolean(),
    holidays: z.string().min(1),
    transfers: z.string().min(1),
    councilTax: z.string().min(1),
    travel: z.string().min(1),
    groceries: z.string().min(1),
    householdShopping: z.string().min(1),
  }),
  propertyAndFinance: z.object({
    propertyLocation: z.enum(['England', 'Scotland', 'Wales']),
    additionalInformation: z.string().optional(),
    signedBy: z.string().min(1),
    downPayment: z.string().min(1),
    isPropertyMainResidence: z.boolean().optional(),
    estimatedPropertyValue: z.string().min(1),
    financePurpose: z.enum(['New Purchase', 'Refinance Buy-to-let Property', 'Other']),
    productType: z.enum(['Buy-to-Let Purchase Plan', 'Home Provision Scheme']),
    termYears: z.string().regex(new RegExp('\\b([1-9]|[12][0-9]|30)\\b')),
  }),
  applicantOneEmploymentDetails: z.object({
    employmentType: z.enum(['Employed', 'Self-employed', 'Contractor', 'Retired', 'No employment']),
    netIncomePreviousYear: z.string().optional(),
    grossAnnualIncome: z.string().optional(),
    grossIncomePreviousYear: z.string().optional(),
    otherEmploymentDetails: z.string().optional(),
    yearsOfIncomeProof: z.enum(['1 year', '2 years+']).optional(),
    otherIncomeTypes: z.array(z.string()).optional(),
    employedType: z.enum(['Permanent', 'Temporary']).optional(),
    anyOtherIncome: z.boolean().optional(),
    employmentStartDate: z.string().optional(),
    willContractBeRenewed: z.boolean().optional(),
    tradingLengthYears: z.string().optional(),
    netMonthlyIncome: z.string().optional(),
    anyOtherEmployment: z.boolean().optional(),
    grossIncomeRecentYear: z.string().optional(),
    monthsLeftOnContract: z.enum(['Under 3 months', '3-6 months', '6-12 months', '12 months+']).optional(),
    totalOtherIncomeAverage: z.string().optional(),
    netIncomeRecentYear: z.string().optional(),
  }),
});

export const submitDipApplicationMutationResponseSchema = z.lazy(() => submitDipApplication200Schema);
